import { useState, useCallback } from 'react';

type SnackbarSeverity = 'success' | 'error' | 'info' | 'warning';

interface UseSnackbarReturn {
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: SnackbarSeverity;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  /** A success message with an Undo button, for a step that can be taken back (it stays up a little longer). */
  showUndo: (message: string, onUndo: () => void) => void;
  snackbarUndo: (() => void) | null;
  hideSnackbar: () => void;
}

export function useSnackbar(): UseSnackbarReturn {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [snackbarUndo, setSnackbarUndo] = useState<(() => void) | null>(null);

  const showSnackbar = useCallback((message: string, severity: SnackbarSeverity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarUndo(null);
    setSnackbarOpen(true);
  }, []);

  const showUndo = useCallback((message: string, onUndo: () => void) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('success');
    setSnackbarUndo(() => onUndo);
    setSnackbarOpen(true);
  }, []);

  const showSuccess = useCallback(
    (message: string) => {
      showSnackbar(message, 'success');
    },
    [showSnackbar]
  );

  const showError = useCallback(
    (message: string) => {
      showSnackbar(message, 'error');
    },
    [showSnackbar]
  );

  const showInfo = useCallback(
    (message: string) => {
      showSnackbar(message, 'info');
    },
    [showSnackbar]
  );

  const showWarning = useCallback(
    (message: string) => {
      showSnackbar(message, 'warning');
    },
    [showSnackbar]
  );

  const hideSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  return {
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showUndo,
    snackbarUndo,
    hideSnackbar,
  };
}
